import type { RouteRecordRaw } from 'vue-router';

import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { BreadcrumbFactory } from '@/router/utils/breadcrumbFactory.ts';
import { usePoliciesStore } from '@/stores/v2/PoliciesStore.ts';
import { validatePolicyAccess, validatePolicyManagementAccess } from '../utils/navigationGuard.ts';
import { SHARED_TABS } from '@/utils/tabs.ts';

export const policiesRoutes: RouteRecordRaw[] = [
  {
    path: '/policies',
    name: 'policies',
    meta: {
      layout: DefaultLayout,
      breadcrumb: BreadcrumbFactory.static.policies,
      sidebarSection: 'policies',
    },
    component: () => import('@/pages/Policies/PoliciesPage.vue'),
    children: [
      {
        path: '',
        name: 'policies-default',
        redirect: { name: 'policies-my-policies' },
      },
      {
        path: 'my-policies',
        name: 'my-policies',
        meta: {
          breadcrumb: BreadcrumbFactory.static.myPolicies,
        },
        children: [
          {
            path: '',
            name: 'policies-my-policies',
            component: () => import('@/pages/Policies/MyPoliciesPage.vue'),
          },
          {
            path: ':policyId/:versionId',
            name: 'my-policy-version-detail',
            meta: {
              breadcrumb: BreadcrumbFactory.policy(() => usePoliciesStore()),
            },
            component: () => import('@/pages/Policies/MyPolicyDetailPage.vue'),
          },
        ],
      },
      {
        path: 'management',
        name: 'management',
        meta: {
          breadcrumb: BreadcrumbFactory.static.policyManagement,
        },
        beforeEnter: (to, from, next) => {
          validatePolicyManagementAccess(next);
        },
        children: [
          {
            path: '',
            name: 'policies-management',
            component: () => import('@/pages/Policies/PoliciesOverview.vue'),
          },
          {
            path: ':policyId',
            name: 'management-policy',
            meta: {
              breadcrumb: BreadcrumbFactory.policy(() => usePoliciesStore()),
            },
            children: [
              {
                path: '',
                name: 'management-policy-detail',
                component: () => import('@/pages/Policies/PolicyDetailPage.vue'),
              },
              {
                path: 'versions',
                name: 'management-policy-versions',
                redirect: (to) => ({
                  name: 'management-policy-detail',
                  params: { policyId: to.params.policyId },
                  query: { tab: SHARED_TABS.POLICY_VERSION_HISTORY },
                }),
                meta: {
                  breadcrumb: BreadcrumbFactory.policyVersions(() => usePoliciesStore()),
                },
                children: [
                  {
                    path: ':versionId',
                    name: 'management-policy-version-detail',
                    component: () => import('@/pages/Policies/PolicyVersionDetailPage.vue'),
                    meta: {
                      breadcrumb: BreadcrumbFactory.policyVersion(() => usePoliciesStore()),
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'templates-catalog',
        name: 'policies-templates-catalog',
        meta: {
          breadcrumb: BreadcrumbFactory.static.templatesCatalog,
        },
        component: () => import('@/pages/Policies/TemplatesCatalogPage.vue'),
        beforeEnter: (to, from, next) => {
          validatePolicyManagementAccess(next);
        },
      },
      {
        path: 'acknowledgments',
        name: 'policies-acknowledgments',
        meta: {
          breadcrumb: BreadcrumbFactory.static.acknowledgments,
        },
        component: () => import('@/pages/Policies/AcknowledgmentsPage.vue'),
        beforeEnter: (to, from, next) => {
          validatePolicyManagementAccess(next);
        },
      },
    ],
    beforeEnter: async (to, from, next) => {
      return validatePolicyAccess(next);
    },
  },
];
